import { useNavigate, useRouter } from '@tanstack/react-router';

import {
	CalendarSync,
	Info,
	LogIn,
	LogOut,
	MonitorDown,
	ShoppingCart,
	UserRoundPlus,
} from 'lucide-react';

import config from '@/config';
import { USER_ROLE } from '@/constants';
import { AuthCognito, LinkItem } from '@/config.type';

import {
	AUTH_KEY,
	IS_SUBSCRIBED_KEY,
} from '@/views/home/auth/Authentication.hooks';

import styles from './Menu.module.css';

export class MenuButton {
	static SignOut = SignOutButton;
	static SignUp = SignUpButton;
	static SignIn = SignInButton;
	static Subscribe = SubscribeButton;
	static ManageSubscription = ManageSubscriptionButton;
	static Install = InstallButton;
	static Links = Links;
	static About = AboutButton;
	static Favorite = FavoriteButton;
}

function SignOutButton() {
	const router = useRouter();
	const basename = `${router.basepath.replace(/\/$/, '')}/logout`;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return (
			<button
				type={'button'}
				onClick={() => {
					router.navigate({ to: '/drafts' });
				}}
			>
				<span>Sign Out</span>
				<span>
					<LogOut />
				</span>
			</button>
		);
	}

	if (!isAuthenticated) {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth?.config?.clientId}`,
		`logout_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth?.config?.host, '/logout?', redirectSections].join(
		'',
	);

	return (
		<button
			type={'button'}
			onClick={() => {
				const r = confirm('Are you sure you want to sign out?');
				if (r) {
					location.href = redirectUrl;
				}
			}}
		>
			<span>Sign Out</span>
			<span>
				<LogOut />
			</span>
		</button>
	);
}

function SignUpButton() {
	const router = useRouter();
	const basename = `${router.basepath.replace(/\/$/, '')}/callback`;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/signup?', redirectSections].join(
		'',
	);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>Sign Up</span>
			<span>
				<UserRoundPlus />
			</span>
		</button>
	);
}

function FavoriteButton() {
	if (!config.app.favorite) return null;

	return <button>Favorite</button>;
}

function SignInButton() {
	const router = useRouter();
	const basename = `${router.basepath.replace(/\/$/, '')}/callback`;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>Sign In</span>
			<span>
				<LogIn />
			</span>
		</button>
	);
}

function SubscribeButton() {
	const navigate = useNavigate();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (isSubscribed || !isAuthenticated) {
		return null;
	}

	return (
		<button
			type={'button'}
			onClick={() => {
				navigate({
					to: '/subscriptions',
				});
			}}
		>
			<span>Subscribe</span>
			<span>
				<ShoppingCart />
			</span>
		</button>
	);
}

function ManageSubscriptionButton() {
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const auth = config.app.auth as AuthCognito;

	if (!isSubscribed || !isAuthenticated) {
		return null;
	}

	const { customerPortalUrl } = auth.config;
	if (!customerPortalUrl) {
		return null;
	}

	return (
		<button
			type={'button'}
			onClick={() => {
				const r = open(customerPortalUrl, '_blank');
				if (r !== null) {
					r.focus();
				}
			}}
		>
			<span>Manage Subscription</span>
			<span>
				<CalendarSync />
			</span>
		</button>
	);
}

function InstallButton({
	show,
	onClick,
}: InstallButtonProps): React.ReactElement | null {
	if (!show) {
		return null;
	}

	if (!onClick) {
		return null;
	}

	return (
		<button type={'button'} onClick={onClick}>
			<span>Install App</span>
			<span>
				<MonitorDown />
			</span>
		</button>
	);
}

export interface InstallButtonProps {
	show?: boolean;
	onClick?: () => void;
}

function AboutButton({
	onClick,
}: InstallButtonProps): React.ReactElement | null {
	if (!onClick) {
		return null;
	}

	return (
		<button type={'button'} onClick={onClick}>
			<span>About</span>
			<span>
				<Info />
			</span>
		</button>
	);
}

export interface AboutButton {
	onClick?: () => void;
}

function Links({ links }: LinkProps) {
	if (!links || !links.length) {
		return null;
	}
	return (
		<section className={styles['links']}>
			{links
				.filter((l) => l.title && l.url)
				.map((l, i) => {
					const { url, title, icon } = l;
					return (
						<a
							key={i}
							href={url}
							target="_blank"
							rel="external nofollow noopener"
						>
							<h4>{title}</h4>
							<div></div>
						</a>
					);
				})}
		</section>
	);
}

interface LinkProps {
	links: Array<LinkItem> | undefined;
}
